import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Drawer,
  Input,
  PartnerPicker,
  ProductPicker,
  Select,
  Textarea,
  type PickerValue,
} from '@/components/ui'
import { useCreateCase } from '../api/queries'
import type { Category, CreateCaseLineInput, ResponsibleParty } from '../api/types'
import { ALL_CATEGORIES, ALL_RESPONSIBLE_PARTIES, CATEGORY_LABELS, RESPONSIBLE_PARTY_LABELS } from '../utils/stage-meta'

export interface CaseCreateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (caseId: string) => void
}

interface DraftLine extends CreateCaseLineInput {
  key: string
}

function newDraftLine(): DraftLine {
  return { key: crypto.randomUUID(), quantity: '' }
}

/** Drop blank-string fields (untouched inputs default to '') rather than
 *  forwarding them — the backend's Decimal fields (quantity, unit_cost)
 *  reject '' with a 422; other optional string fields should just be
 *  absent, not an empty string, when the operator left them blank. */
function sanitizeLine(line: CreateCaseLineInput): CreateCaseLineInput {
  const out: CreateCaseLineInput = {}
  for (const [key, value] of Object.entries(line)) {
    if (typeof value === 'string' && value.trim() === '') continue
    ;(out as Record<string, unknown>)[key] = value
  }
  return out
}

export function CaseCreateDrawer({ open, onOpenChange, onCreated }: CaseCreateDrawerProps) {
  const create = useCreateCase()

  const [category, setCategory] = useState<Category>('damaged')
  const [vendor, setVendor] = useState<PickerValue>(null)
  const [carrier, setCarrier] = useState('')
  const [bolNumber, setBolNumber] = useState('')
  const [proNumber, setProNumber] = useState('')
  const [responsibleParty, setResponsibleParty] = useState<ResponsibleParty | ''>('')
  const [location, setLocation] = useState('')
  const [discoveredDate, setDiscoveredDate] = useState('')
  const [rootCauseNotes, setRootCauseNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([newDraftLine()])

  function reset() {
    setCategory('damaged')
    setVendor(null)
    setCarrier('')
    setBolNumber('')
    setProNumber('')
    setResponsibleParty('')
    setLocation('')
    setDiscoveredDate('')
    setRootCauseNotes('')
    setLines([newDraftLine()])
  }

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key))
  }

  function submit() {
    const payloadLines = lines
      .filter((l) => l.product_name || l.quantity)
      .map(({ key: _key, ...rest }) => sanitizeLine(rest))

    create.mutate(
      {
        category,
        vendor_id: vendor ? vendor[0] : null,
        vendor_name: vendor ? vendor[1] : null,
        carrier: carrier || null,
        bol_number: bolNumber || null,
        pro_number: proNumber || null,
        responsible_party: responsibleParty || null,
        location: location || null,
        discovered_date: discoveredDate || null,
        root_cause_notes: rootCauseNotes || null,
        lines: payloadLines,
      },
      {
        onSuccess: (result) => {
          toast.success(`Case ${result.case_number} created`)
          reset()
          onOpenChange(false)
          onCreated(result.case_id)
        },
        onError: (err) =>
          toast.error(`Create failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title="New material disposition case"
      meta="Damaged / lost / quarantined / non-sellable material"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={create.isPending} onClick={submit}>
            Create case
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Category</span>
          <Select
            value={category}
            onChange={(e) => {
              const next = e.target.value as Category
              setCategory(next)
              if (next !== 'damaged' && next !== 'lost') {
                setCarrier('')
                setBolNumber('')
                setProNumber('')
              }
            }}
          >
            {ALL_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
        </label>

        {category === 'damaged' || category === 'lost' ? (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary">Carrier (optional)</span>
              <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. FedEx Freight" />
            </label>
            {/* Captured at intake on purpose — the BOL is in the
                receiver's hand when they open the case, and having it
                here is what saves digging through freight records later
                if this turns into a claim. */}
            <div className="flex gap-2">
              <label className="flex flex-1 flex-col gap-1 text-sm">
                <span className="text-secondary">BOL number (optional)</span>
                <Input
                  value={bolNumber}
                  onChange={(e) => setBolNumber(e.target.value)}
                  placeholder="from the paperwork"
                />
              </label>
              <label className="flex flex-1 flex-col gap-1 text-sm">
                <span className="text-secondary">PRO number (optional)</span>
                <Input
                  value={proNumber}
                  onChange={(e) => setProNumber(e.target.value)}
                  placeholder="carrier tracking / PRO"
                />
              </label>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-secondary">Products</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLines((prev) => [...prev, newDraftLine()])}
            >
              <Plus size={12} /> Add product
            </Button>
          </div>
          {lines.map((line) => (
            <div key={line.key} className="flex items-end gap-2 rounded-sm border border-border-subtle p-2">
              <div className="flex-1">
                <ProductPicker
                  value={line.product_id ? [line.product_id, line.product_name ?? ''] : null}
                  onChange={(next) =>
                    updateLine(line.key, {
                      product_id: next ? next[0] : null,
                      product_name: next ? next[1] : null,
                    })
                  }
                  width={200}
                />
              </div>
              <Input
                value={line.quantity ?? ''}
                onChange={(e) => updateLine(line.key, { quantity: e.target.value })}
                placeholder="Qty"
                className="w-20"
              />
              <Input
                value={line.uom ?? ''}
                onChange={(e) => updateLine(line.key, { uom: e.target.value })}
                placeholder="UoM"
                className="w-16"
              />
              <Input
                value={line.unit_cost ?? ''}
                onChange={(e) => updateLine(line.key, { unit_cost: e.target.value })}
                placeholder="Unit cost"
                className="w-24"
              />
              <Input
                value={line.lot_number ?? ''}
                onChange={(e) => updateLine(line.key, { lot_number: e.target.value })}
                placeholder="Lot #"
                className="w-24"
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Remove product"
                onClick={() => removeLine(line.key)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Vendor (optional)</span>
          <PartnerPicker value={vendor} onChange={setVendor} kind="vendor" />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Responsible party</span>
          <Select
            value={responsibleParty}
            onChange={(e) => setResponsibleParty(e.target.value as ResponsibleParty | '')}
          >
            <option value="">Not yet known</option>
            {ALL_RESPONSIBLE_PARTIES.map((p) => (
              <option key={p} value={p}>
                {RESPONSIBLE_PARTY_LABELS[p]}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Location found (optional)</span>
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Warehouse A, Rack 3" />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Date discovered (optional)</span>
          <Input type="date" value={discoveredDate} onChange={(e) => setDiscoveredDate(e.target.value)} />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Root cause notes</span>
          <Textarea
            value={rootCauseNotes}
            onChange={(e) => setRootCauseNotes(e.target.value)}
            rows={3}
            placeholder="What happened, and how it was found…"
          />
        </label>
      </div>
    </Drawer>
  )
}
